/**
 * The public, read-only render of a published page (/s/:token, signed-out visitors): the page's current body as
 * block views (the same BlockView the editor renders, never editable), its published subpages and breadcrumbs, and
 * whether search engines may index it (the UI sets `noindex` otherwise). No workspace data beyond the page.
 */
import type { PublicPage, PublicPageResponse } from "@bundle/contracts";
import { visibleRows, type EditorSnapshot } from "../logic/editor.ts";
import { errorKind, type SharingApi } from "../ports.ts";
import type { BlockView, PageRefView, RunView } from "./PageViewModel.ts";
import { ViewModel } from "./ViewModel.ts";

export type PublicPageState = {
  status: "loading" | "ready" | "notFound" | "rateLimited" | "error";
  token: string;
  /** The subpage being viewed (absent: the published page itself). */
  pageId?: string;
  page?: PublicPage;
  blocks: BlockView[];
  subpages: PublicPage[];
  breadcrumbs: PublicPage[];
  /** False ⇒ the page must carry `noindex` (the default). */
  allowIndexing: boolean;
  allowDuplicate: boolean;
  updatedAt?: number;
};

export class PublicPageViewModel extends ViewModel<PublicPageState> {
  private readonly api: SharingApi;
  private res: PublicPageResponse | undefined;
  /** Closed toggles (everything starts open on a public page). */
  private closed: string[] = [];

  constructor(api: SharingApi, token: string, pageId?: string) {
    super({ status: "loading", token, blocks: [], subpages: [], breadcrumbs: [], allowIndexing: false, allowDuplicate: false, ...(pageId === undefined ? {} : { pageId }) });
    this.api = api;
    void this.load(pageId);
  }

  /** Opens a published subpage (undefined: back to the published page). */
  open(pageId: string | undefined): Promise<void> {
    return this.load(pageId);
  }

  toggle(blockId: string): void {
    this.closed = this.closed.includes(blockId) ? this.closed.filter((id) => id !== blockId) : [...this.closed, blockId];
    this.update({ blocks: this.views() });
  }

  private async load(pageId: string | undefined): Promise<void> {
    const s = { ...this.state, status: "loading" as const };
    if (pageId === undefined) delete s.pageId;
    else s.pageId = pageId;
    this.setState(s);
    try {
      this.res = await this.api.publicPage(this.state.token, pageId);
      this.closed = [];
      const r = this.res;
      this.setState({
        ...this.state,
        status: "ready",
        page: r.page,
        blocks: this.views(),
        subpages: r.subpages,
        breadcrumbs: r.breadcrumbs,
        allowIndexing: r.allowIndexing,
        allowDuplicate: r.allowDuplicate,
        updatedAt: r.updatedAt,
      });
    } catch (e) {
      const kind = errorKind(e);
      this.update({ status: kind === "notFound" ? "notFound" : kind === "rateLimited" ? "rateLimited" : "error" });
    }
  }

  private views(): BlockView[] {
    const r = this.res;
    if (r === undefined) return [];
    const doc = r.body as EditorSnapshot;
    const titles = new Map<string, string>([[r.page.id, r.page.title], ...r.subpages.map((p) => [p.id, p.title] as const), ...r.breadcrumbs.map((p) => [p.id, p.title] as const)]);
    return visibleRows(doc, this.closed).map((row) => {
      const b = doc.blocks[row.id]!;
      const runs: RunView[] = b.spans.map((s) => (s.mention === undefined ? s : { ...s, label: s.mention.id === undefined ? "" : (titles.get(s.mention.id) ?? "") }));
      const v: BlockView = {
        id: b.id,
        type: b.type,
        depth: row.depth,
        props: b.props,
        text: b.text,
        runs,
        placeholderAlways: false,
        hasChildren: b.children.length > 0,
        collapsed: b.type === "toggle" && this.closed.includes(b.id),
        selected: false,
        carets: [],
      };
      if (row.number !== undefined) v.number = row.number;
      if (b.type === "child_page" || b.type === "link_to_page" || b.type === "child_database") {
        const id = b.props.pageId;
        const ref: PageRefView = { id: id ?? "", title: id === undefined ? "" : (titles.get(id) ?? ""), kind: b.type === "child_database" ? "database" : "page", missing: id === undefined };
        v.ref = ref;
      }
      return v;
    });
  }
}
